import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { Link } from 'react-router-dom';
import { legalService, FinancialDashboard, MATTER_TYPE_LABELS } from '../services/legal.service';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

export default function LegalFinancialDashboard() {
  const [data, setData] = useState<FinancialDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    legalService
      .getFinancialDashboard()
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((err) => {
        if (!cancelled) {setError(err instanceof Error ? err.message : 'Failed to load dashboard');}
      })
      .finally(() => { if (!cancelled) {setLoading(false);} });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center text-secondary-600 dark:text-secondary-400 py-12">
          Loading financial dashboard...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error Loading Dashboard
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error ?? 'Could not load financial data'}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Legal Financial Dashboard
        </h1>
        <p className="text-secondary-600 dark:text-secondary-400 mt-1">
          Financial overview across all legal matters
        </p>
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <CurrencyDollarIcon className="w-6 h-6 text-primary-500" />
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  Total Legal Spend
                </span>
              </div>
              <div className="text-4xl font-bold text-secondary-900 dark:text-white">
                {legalService.formatCurrency(data.totalSpend)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <CurrencyDollarIcon className="w-6 h-6 text-warning-500" />
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  Total Reserves
                </span>
              </div>
              <div className="text-4xl font-bold text-secondary-900 dark:text-white">
                {legalService.formatCurrency(data.totalReserves)}
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spend by Type */}
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardHeader>
              <CardTitle>Spend by Matter Type</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {data.byType.length === 0 ? (
                <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
                  No data available.
                </p>
              ) : (
                <div className="space-y-3">
                  {data.byType.map((row) => {
                    const pct =
                      data.totalSpend > 0 ? (Number(row.total) / data.totalSpend) * 100 : 0;
                    return (
                      <div key={row.type}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-secondary-700 dark:text-secondary-300 truncate pr-2">
                            {MATTER_TYPE_LABELS[row.type] ?? row.type}
                          </span>
                          <span className="font-mono font-medium text-secondary-900 dark:text-white flex-shrink-0">
                            {legalService.formatCurrency(row.total)}
                          </span>
                        </div>
                        <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-primary-500"
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                          {row.count} matter{row.count !== 1 ? 's' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Top Matters by Spend */}
        <motion.div variants={ITEM_VARIANTS}>
          <Card>
            <CardHeader>
              <CardTitle>Top Matters by Spend</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {data.topMatters.length === 0 ? (
                <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
                  No data available.
                </p>
              ) : (
                <ol className="space-y-3">
                  {data.topMatters.map((m, i) => (
                    <li key={m.id} className="flex items-center gap-3">
                      <span className="text-lg font-bold text-secondary-400 dark:text-secondary-600 w-6 text-right flex-shrink-0">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/legal/matters/${m.id}`}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline truncate block"
                        >
                          {m.title}
                        </Link>
                        <span className="text-xs font-mono text-secondary-500 dark:text-secondary-400">
                          {m.matterNumber}
                        </span>
                      </div>
                      <span className="font-mono font-semibold text-secondary-900 dark:text-white flex-shrink-0">
                        {legalService.formatCurrency(m.total)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Budget Variance Table */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardHeader>
            <CardTitle>Budget Variance</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {(!data.budgetVariance || data.budgetVariance.length === 0) ? (
              <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
                No budget data available.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-secondary-200 dark:border-secondary-700">
                      <th className="text-left py-2 pr-4 text-secondary-600 dark:text-secondary-400 font-medium">Matter</th>
                      <th className="text-right py-2 pr-4 text-secondary-600 dark:text-secondary-400 font-medium">Budget</th>
                      <th className="text-right py-2 pr-4 text-secondary-600 dark:text-secondary-400 font-medium">Actual</th>
                      <th className="text-right py-2 pr-4 text-secondary-600 dark:text-secondary-400 font-medium">Variance</th>
                      <th className="py-2 text-secondary-600 dark:text-secondary-400 font-medium">Alert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.budgetVariance.map((row) => {
                      const isOverBudget = row.variance !== null && row.variance !== undefined && row.variance < 0;
                      const isNearBudget =
                        row.variance !== null &&
                        row.variance !== undefined &&
                        row.variance >= 0 &&
                        row.budget !== null &&
                        row.budget !== undefined &&
                        row.budget > 0 &&
                        row.variance / row.budget < 0.2;

                      return (
                        <tr
                          key={row.id}
                          className="border-b border-secondary-100 dark:border-secondary-800 hover:bg-secondary-50 dark:hover:bg-secondary-800/50"
                        >
                          <td className="py-3 pr-4">
                            <Link
                              to={`/legal/matters/${row.id}`}
                              className="text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {row.title}
                            </Link>
                            <div className="text-xs font-mono text-secondary-500 dark:text-secondary-400">
                              {row.matterNumber}
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-right font-mono text-secondary-700 dark:text-secondary-300">
                            {legalService.formatCurrency(row.budget)}
                          </td>
                          <td className="py-3 pr-4 text-right font-mono text-secondary-900 dark:text-white">
                            {legalService.formatCurrency(row.actual)}
                          </td>
                          <td
                            className={cn(
                              'py-3 pr-4 text-right font-mono font-medium',
                              isOverBudget
                                ? 'text-danger-600 dark:text-danger-400'
                                : 'text-success-600 dark:text-success-400'
                            )}
                          >
                            <span className="flex items-center justify-end gap-1">
                              {row.variance !== null && row.variance !== undefined ? (
                                <>
                                  {row.variance < 0 ? (
                                    <ArrowUpIcon className="w-3 h-3" />
                                  ) : (
                                    <ArrowDownIcon className="w-3 h-3" />
                                  )}
                                  {legalService.formatCurrency(Math.abs(row.variance))}
                                </>
                              ) : (
                                '—'
                              )}
                            </span>
                          </td>
                          <td className="py-3">
                            {isOverBudget ? (
                              <span className="px-2 py-0.5 text-xs bg-danger-100 dark:bg-danger-900/20 text-danger-800 dark:text-danger-300 rounded-full">
                                Over Budget
                              </span>
                            ) : isNearBudget ? (
                              <span className="px-2 py-0.5 text-xs bg-warning-100 dark:bg-warning-900/20 text-warning-800 dark:text-warning-300 rounded-full">
                                Near Budget
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
